if (!customElements.get('oaklen-product-gallery')) {
  customElements.define(
    'oaklen-product-gallery',
    class OaklenProductGallery extends HTMLElement {
      constructor() {
        super();
        this.activeIndex = 0;
        this.unsubscribeVariantChange = null;
        this.previousBodyOverflow = '';
        this.onKeydown = this.onKeydown.bind(this);
      }

      connectedCallback() {
        this.slides = Array.from(
          this.querySelectorAll('[data-oaklen-gallery-slide]'),
        );
        this.thumbs = Array.from(
          this.querySelectorAll('[data-oaklen-gallery-thumb]'),
        );
        this.currentCounters = Array.from(
          this.querySelectorAll('[data-oaklen-gallery-current]'),
        );
        this.lightboxCurrentCounters = Array.from(
          this.querySelectorAll('[data-oaklen-gallery-lightbox-current]'),
        );
        this.lightbox = this.querySelector('[data-oaklen-gallery-lightbox]');
        this.lightboxImage = this.querySelector(
          '[data-oaklen-gallery-lightbox-image]',
        );

        if (!this.slides.length) return;

        this.activeIndex = Math.max(
          0,
          this.slides.findIndex((slide) =>
            slide.classList.contains('is-active'),
          ),
        );

        this.querySelector('[data-oaklen-gallery-prev]')?.addEventListener(
          'click',
          () => this.go(-1),
        );
        this.querySelector('[data-oaklen-gallery-next]')?.addEventListener(
          'click',
          () => this.go(1),
        );
        this.querySelector('[data-oaklen-gallery-open]')?.addEventListener(
          'click',
          () => this.openLightbox(),
        );
        this.querySelector('[data-oaklen-gallery-close]')?.addEventListener(
          'click',
          () => this.closeLightbox(),
        );
        this.querySelector(
          '[data-oaklen-gallery-lightbox-prev]',
        )?.addEventListener('click', () => this.go(-1));
        this.querySelector(
          '[data-oaklen-gallery-lightbox-next]',
        )?.addEventListener('click', () => this.go(1));

        this.thumbs.forEach((thumb) => {
          thumb.addEventListener('click', () => {
            this.setActive(Number(thumb.dataset.index || 0));
          });
        });

        this.lightbox?.addEventListener('click', (event) => {
          if (event.target === this.lightbox) this.closeLightbox();
        });

        if (
          typeof subscribe === 'function' &&
          typeof PUB_SUB_EVENTS !== 'undefined'
        ) {
          this.unsubscribeVariantChange = subscribe(
            PUB_SUB_EVENTS.variantChange,
            ({ data }) => {
              if (!data?.variant?.featured_media?.id) return;
              if (data.sectionId !== this.dataset.sectionId) return;
              this.setActiveByMediaId(
                `${this.dataset.sectionId}-${data.variant.featured_media.id}`,
              );
            },
          );
        }

        this.setActive(this.activeIndex);
      }

      disconnectedCallback() {
        this.unsubscribeVariantChange?.();
        document.removeEventListener('keydown', this.onKeydown);
        this.classList.remove('is-lightbox-open');
        this.restoreBodyScroll();
      }

      go(direction) {
        const nextIndex =
          (this.activeIndex + direction + this.slides.length) %
          this.slides.length;
        this.setActive(nextIndex);
      }

      setActiveByMediaId(mediaId) {
        const index = this.slides.findIndex(
          (slide) => slide.dataset.mediaId === mediaId,
        );
        if (index >= 0) this.setActive(index);
      }

      setActive(index) {
        this.activeIndex = Math.max(0, Math.min(index, this.slides.length - 1));

        this.slides.forEach((slide, slideIndex) => {
          slide.classList.toggle('is-active', slideIndex === this.activeIndex);
        });

        this.thumbs.forEach((thumb) => {
          const isActive =
            Number(thumb.dataset.index || 0) === this.activeIndex;
          thumb.classList.toggle('is-active', isActive);
          thumb.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });

        const current = String(this.activeIndex + 1);
        this.currentCounters.forEach((counter) => {
          counter.textContent = current;
        });
        this.lightboxCurrentCounters.forEach((counter) => {
          counter.textContent = current;
        });

        this.updateLightboxImage();
      }

      openLightbox() {
        if (!this.lightbox || !this.lightboxImage) return;
        this.updateLightboxImage();
        this.lightbox.hidden = false;
        this.classList.add('is-lightbox-open');
        this.previousBodyOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', this.onKeydown);
        this.querySelector('[data-oaklen-gallery-close]')?.focus();
      }

      closeLightbox() {
        if (!this.lightbox) return;
        this.lightbox.hidden = true;
        this.classList.remove('is-lightbox-open');
        document.removeEventListener('keydown', this.onKeydown);
        this.restoreBodyScroll();
      }

      restoreBodyScroll() {
        document.body.style.overflow = this.previousBodyOverflow;
      }

      updateLightboxImage() {
        if (!this.lightboxImage) return;
        const activeSlide = this.slides[this.activeIndex];
        if (!activeSlide) return;
        this.lightboxImage.src = activeSlide.dataset.fullSrc || '';
        this.lightboxImage.alt = activeSlide.dataset.alt || '';
      }

      onKeydown(event) {
        if (this.lightbox?.hidden) return;
        if (event.key === 'Escape') this.closeLightbox();
        if (event.key === 'ArrowLeft') this.go(-1);
        if (event.key === 'ArrowRight') this.go(1);
      }
    },
  );
}

(() => {
  const buyNowSelector = '[data-oaklen-product-buy-now]';

  const updateBuyNowUrl = (productInfo) => {
    const buyNowLink = productInfo?.querySelector(buyNowSelector);
    const productForm = buyNowLink?.closest('form');
    const variantInput = productForm?.querySelector('input[name="id"]');
    const quantityInput = productInfo?.querySelector(
      `input[name="quantity"][form="${productForm?.id}"]`,
    );
    const submitButton = productForm?.querySelector('[name="add"]');
    const variantId = variantInput?.value;
    const quantity = Math.max(parseInt(quantityInput?.value, 10) || 1, 1);
    const cartUrl = buyNowLink?.dataset.cartUrl;
    const isDisabled = !variantId || submitButton?.disabled;

    if (!buyNowLink) return;

    if (isDisabled) {
      buyNowLink.setAttribute('aria-disabled', 'true');
    } else {
      buyNowLink.removeAttribute('aria-disabled');
    }

    buyNowLink.tabIndex = isDisabled ? -1 : 0;

    if (!isDisabled && cartUrl) {
      buyNowLink.href = `${cartUrl}/${variantId}:${quantity}`;
    }
  };

  document.addEventListener('change', (event) => {
    if (
      event.target.matches('.oaklen-main-product__quantity-input') ||
      event.target.matches('.oaklen-main-product .product-variant-id')
    ) {
      updateBuyNowUrl(event.target.closest('product-info'));
    }
  });

  document.addEventListener('click', (event) => {
    const buyNowLink = event.target.closest(buyNowSelector);

    if (!buyNowLink) return;

    const productInfo = buyNowLink.closest('product-info');
    updateBuyNowUrl(productInfo);

    if (buyNowLink.getAttribute('aria-disabled') === 'true') {
      event.preventDefault();
    }
  });

  if (
    typeof subscribe === 'function' &&
    typeof PUB_SUB_EVENTS !== 'undefined'
  ) {
    subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
      const productInfo = document.querySelector(
        `product-info[data-section="${data?.sectionId}"]`,
      );

      updateBuyNowUrl(productInfo);
    });
  }
})();
